import { useCallback, useEffect, useRef, useState } from 'react';
import type { WSClientMessage, WSServerMessage } from '@cartiq/shared';
import { WS_URL } from '../config';
import type { ConnectionState } from '../types';

const SESSION_KEY_PREFIX = 'cartiq:session:';

function getOrCreateSessionId(slug: string): string {
  const key = `${SESSION_KEY_PREFIX}${slug}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

interface UseWebSocketResult {
  connectionState: ConnectionState;
  sessionId: string;
  /** Latest received message (consumers should react to changes). */
  lastMessage: WSServerMessage | null;
  send: (msg: WSClientMessage) => void;
}

/**
 * Connect to the chat websocket with auto-reconnect (exponential backoff).
 * The sessionId is persisted in sessionStorage so a refresh resumes the chat.
 */
export function useWebSocket(slug: string, enabled: boolean): UseWebSocketResult {
  const sessionIdRef = useRef<string>(getOrCreateSessionId(slug));
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [lastMessage, setLastMessage] = useState<WSServerMessage | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const manualClose = useRef(false);

  const connect = useCallback(() => {
    if (!enabled) return;
    manualClose.current = false;

    const url = `${WS_URL}/ws/chat/${encodeURIComponent(slug)}?sessionId=${encodeURIComponent(
      sessionIdRef.current,
    )}`;

    setConnectionState(reconnectAttempts.current === 0 ? 'connecting' : 'reconnecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttempts.current = 0;
      setConnectionState('open');
      // Heartbeat keeps the connection alive through proxies.
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' } satisfies WSClientMessage));
        }
      }, 25000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WSServerMessage;
        if (data.type === 'pong') return;
        setLastMessage(data);
      } catch {
        // Ignore malformed frames.
      }
    };

    ws.onclose = () => {
      if (pingTimer.current) clearInterval(pingTimer.current);
      pingTimer.current = null;
      if (manualClose.current) {
        setConnectionState('closed');
        return;
      }
      scheduleReconnect();
    };

    ws.onerror = () => {
      ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enabled]);

  const scheduleReconnect = useCallback(() => {
    setConnectionState('reconnecting');
    const attempt = reconnectAttempts.current++;
    const delay = Math.min(1000 * 2 ** attempt, 15000);
    reconnectTimer.current = setTimeout(connect, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  useEffect(() => {
    if (!enabled) return;
    connect();
    return () => {
      manualClose.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect, enabled]);

  const send = useCallback((msg: WSClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  return {
    connectionState,
    sessionId: sessionIdRef.current,
    lastMessage,
    send,
  };
}
